import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { confirmStaffQuarterWillingness, getStaffQuarterDetails, getStaffQuarterPermissions, withdrawStaffQuarterOccupation, downloadAllotmentLetter, getPreferredLocations, updateLocationPreferences } from "../../../../core/data/redux/staffQuarterSlice";
import ApproverTracker from "../components/ApproverTracker";
import { Spin, Modal, Form, Input, Button, Checkbox, Radio, Space, Tabs } from "antd";
import { toast } from "react-toastify";
import { decodeRouteParam } from "../../../../core/hooks/routeEncoder";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";
import AllotmentLetterCustomizerModal from "../components/AllotmentLetterCustomizerModal";

const StaffQuarterDetails: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const { id: encodedId } = useParams<{ id: string }>();
  const id = decodeRouteParam(encodedId || "");
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  const currentUser = useSelector((state: any) => state.authState.user?.data?.user);
  const apiData = staffQuarterState?.currentRequest?.data;
  const isLoading = staffQuarterState.loading === "pending";
  const isConfirming = staffQuarterState.action === "confirm-willingness" && staffQuarterState.loading === "pending";

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedWillingness, setSelectedWillingness] = React.useState<string>("YES");
  const [selectedWillingnessRef, setSelectedWillingnessRef] = React.useState<string | null>(null);
  const [form] = Form.useForm();
  const [activeTabKey, setActiveTabKey] = React.useState<string>("registration");
  const [activeWillingnessTabKey, setActiveWillingnessTabKey] = React.useState<string>("0");
  const [selectedWillingnessLocTab, setSelectedWillingnessLocTab] = React.useState<string>("ALL");
  const lastLoadedIdRef = React.useRef<string | null>(null);
  const lastSelectedRef = React.useRef<string | null>(null);
  const [downloadingLetter, setDownloadingLetter] = React.useState(false);
  const [letterCustomizerOpen, setLetterCustomizerOpen] = React.useState(false);

  const handleDownloadAllotmentLetter = () => {
    const registrationId = apiData?.registrationId || apiData?.id || apiData?.allotmentDetails?.registrationId || id;
    if (!registrationId) {
      toast.error("Registration ID not found.");
      return;
    }
    setDownloadingLetter(true);
    dispatch(downloadAllotmentLetter({ registrationId }))
      .then((res: any) => {
        if (res.payload) {
          const blob = res.payload instanceof Blob
            ? res.payload
            : new Blob([res.payload], { type: 'application/pdf' });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `allotment-letter_${registrationId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success("Allotment letter downloaded successfully!");
        } else {
          toast.error("Failed to download allotment letter.");
        }
      })
      .catch(() => {
        toast.error("An error occurred while downloading the allotment letter.");
      })
      .finally(() => {
        setDownloadingLetter(false);
      });
  };

  const [showLocationModal, setShowLocationModal] = React.useState(false);
  const [isFetchingLocations, setIsFetchingLocations] = React.useState(false);
  const [isUpdatingLocations, setIsUpdatingLocations] = React.useState(false);
  const [availableLocations, setAvailableLocations] = React.useState<any[]>([]);
  const [modalPrefs, setModalPrefs] = React.useState({ pref1: "", pref2: "", pref3: "" });
  const [modalErrors, setModalErrors] = React.useState<any>({});

  useEffect(() => {
    if (showLocationModal && apiData) {
      setIsFetchingLocations(true);
      dispatch(getPreferredLocations({
        poolTypeId: apiData.pool?.id,
        quarterTypeCode: apiData.quarterTypeEligibility?.quarterTypeCode || apiData.quarterTypeEligibility?.quarter_type_code || apiData.quarterTypeEligibility?.code || apiData.quarterTypeEligibility?.quarterType || null
      })).then((res: any) => {
        setIsFetchingLocations(false);
        const locs = res.payload?.data?.data || res.payload?.data || res.payload;
        if (Array.isArray(locs)) {
          setAvailableLocations(locs);
        } else {
          setAvailableLocations([]);
        }
      }).catch(() => {
        setIsFetchingLocations(false);
        setAvailableLocations([]);
      });

      const currentPrefs = [...(apiData.locationPreferences || [])].sort((a: any, b: any) => a.preference - b.preference);
      setModalPrefs({
        pref1: currentPrefs.find((p: any) => p.preference === 1)?.id?.toString() || "",
        pref2: currentPrefs.find((p: any) => p.preference === 2)?.id?.toString() || "",
        pref3: currentPrefs.find((p: any) => p.preference === 3)?.id?.toString() || ""
      });
    }
  }, [showLocationModal, apiData, dispatch]);

  const handleUpdateLocations = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiData) return;
    if (!modalPrefs.pref1) {
      setModalErrors({ submit: "First location preference is required." });
      return;
    }
    setIsUpdatingLocations(true);
    setModalErrors({});
    dispatch(updateLocationPreferences({
      registrationId: apiData.id,
      locationPreference1Id: Number(modalPrefs.pref1),
      locationPreference2Id: modalPrefs.pref2 ? Number(modalPrefs.pref2) : null,
      locationPreference3Id: modalPrefs.pref3 ? Number(modalPrefs.pref3) : null
    })).then((res: any) => {
      setIsUpdatingLocations(false);
      if (res.payload?.success) {
        toast.success("Location preferences updated successfully!");
        setShowLocationModal(false);
        dispatch(getStaffQuarterDetails({ id: apiData.id }));
      } else {
        toast.error(res.payload?.message || "Failed to update location preferences.");
        setModalErrors({ submit: res.payload?.message || "Failed to update location preferences." });
      }
    }).catch((err: any) => {
      setIsUpdatingLocations(false);
      toast.error(err?.message || "An error occurred while updating preferences.");
      setModalErrors({ submit: err?.message || "An error occurred." });
    });
  };

  useEffect(() => {
    if (apiData && Array.isArray(apiData.willingnessDetails)) {
      const currentId = String(apiData.id || "");
      if (lastLoadedIdRef.current !== currentId || lastSelectedRef.current !== selectedWillingnessRef) {
        lastLoadedIdRef.current = currentId;
        lastSelectedRef.current = selectedWillingnessRef;
        const list = apiData.willingnessDetails;
        if (list.length > 0) {
          if (selectedWillingnessRef) {
            const matchedIdx = list.findIndex((wd: any) => String(wd.referenceNumber) === String(selectedWillingnessRef));
            if (matchedIdx !== -1) {
              setActiveWillingnessTabKey(String(matchedIdx));
              return;
            }
          }
          const pendingIdx = list.findIndex((wd: any) => wd.willingnessStatus === "PENDING");
          if (pendingIdx !== -1) {
            setActiveWillingnessTabKey(String(pendingIdx));
            return;
          }
          setActiveWillingnessTabKey("0");
        }
      }
    }
  }, [apiData, selectedWillingnessRef]);

  useEffect(() => {
    if (apiData) {
      const wList = Array.isArray(apiData?.willingnessDetails)
        ? apiData.willingnessDetails
        : apiData?.willingnessDetails ? [apiData.willingnessDetails] : [];
      const hasW = wList.length > 0;
      const hasO = !!(apiData.allotmentDetails || apiData.occupationDetails);
      if (hasW) {
        setActiveTabKey("willingness");
      } else if (hasO) {
        setActiveTabKey("occupation");
      } else {
        setActiveTabKey("registration");
      }
    }
  }, [apiData]);



  const sqPermissions = staffQuarterState?.permissions;
  const sqLoading = staffQuarterState?.loading;

  useEffect(() => {
    if (!sqPermissions) {
      dispatch(getStaffQuarterPermissions({}));
    }
  }, [dispatch, sqPermissions]);

  const canViewWillingness = !sqPermissions || !Array.isArray(sqPermissions) ||
    sqPermissions.find((p: any) => p.module === "willingness")?.canView !== false;

  useEffect(() => {
    if (id) {
      dispatch(getStaffQuarterDetails({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const isWillingnessOrWithdrawal = staffQuarterState.action === "confirm-willingness" || staffQuarterState.action === "withdraw-occupation";
    if (isWillingnessOrWithdrawal && staffQuarterState.loading === "fulfilled") {
      toast.success("Request processed successfully.");
      setIsModalOpen(false);
      form.resetFields();
      setSelectedWillingness("YES");
      setSelectedWillingnessRef(null);
      if (id) dispatch(getStaffQuarterDetails({ id }));
    } else if (isWillingnessOrWithdrawal && staffQuarterState.loading === "rejected") {
      toast.error(staffQuarterState.error || "Failed to process request.");
    }
  }, [staffQuarterState.loading, staffQuarterState.action, staffQuarterState.error, dispatch, id, form]);

  const handleWillingnessSubmit = (values: any) => {
    const payload = {
      willingnessReferenceNumber: selectedWillingnessRef || apiData?.willingnessDetails?.referenceNumber,
      isWilling: values.isWilling === "YES",
      remarks: values.remarks || "",
    };
    dispatch(confirmStaffQuarterWillingness(payload));
  };

  const isFetchComplete = staffQuarterState.action === "get-details" && staffQuarterState.loading === "fulfilled";
  const isFetchFailed = staffQuarterState.loading === "rejected";
  const isNotFound = (isFetchComplete && !apiData) || isFetchFailed;

  if (!apiData && !isNotFound) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
          <Spin size="large" tip="Loading Application Details..." />
        </div>
      </div>
    );
  }

  if (!apiData && isNotFound) {
    return (
      <div className="page-wrapper">
        <div className="content p-4">
          <div className="alert alert-warning border-0 shadow-sm rounded-3">
            <i className="ti ti-alert-triangle me-2"></i>
            Application not found.
          </div>
          <Link to={routes.staffQuarter?.list || "#"} className="btn btn-primary">Back to List</Link>
        </div>
      </div>
    );
  }

  const data = apiData;
  const willingnessList = Array.isArray(data?.willingnessDetails)
    ? data.willingnessDetails
    : data?.willingnessDetails ? [data.willingnessDetails] : [];
  const approvedWillingness = willingnessList.find((wd: any) => {
    const statusUpper = (wd.willingnessStatus || "").toUpperCase();
    return wd.isWilling === 'YES' || ['APPROVED', 'ALLOTTED', 'ALLOTED', 'CONFIRMED'].includes(statusUpper);
  });
  const activeWillingnessDetail = willingnessList.find((wd: any) => wd.referenceNumber === selectedWillingnessRef) || approvedWillingness || willingnessList[0];
  const hasWillingnessData = willingnessList.length > 0;
  const hasAllotmentData = !!(data?.allotmentDetails || data?.allotment || data?.occupationDetails);
  const hasOccupationData = !!(data?.occupationDetails || data?.allotmentDetails);
  const financeObj = data?.financeDetails || data?.finance || data?.financeData || null;
  const hasFinanceData = !!hasAllotmentData && !!financeObj && !!(
    financeObj.id ||
    financeObj.referenceNumber ||
    financeObj.status ||
    financeObj.hraAmount ||
    financeObj.deductionType ||
    financeObj.effectiveDate ||
    (Array.isArray(financeObj.approvers) && financeObj.approvers.length > 0)
  );
  const financeApprovers = financeObj?.approvals || financeObj?.approverDetails || financeObj?.approvers || [];

  const pendingWillingness = willingnessList.find((wd: any) => {
    const statusUpper = (wd.willingnessStatus || "").toUpperCase();
    const isWilling = wd.isWilling === 'YES' || statusUpper === 'CONFIRMED' || statusUpper === 'APPROVED' || statusUpper === 'ALLOTTED' || statusUpper === 'ALLOTED';
    const isNotWilling = wd.isWilling === 'NO' || statusUpper === 'DECLINED' || statusUpper === 'REJECTED';
    return !isWilling && !isNotWilling;
  });

  const hasDeclinedWillingness = willingnessList.some((wd: any) => {
    const statusUpper = (wd.willingnessStatus || "").toUpperCase();
    return wd.isWilling === 'NO' || ['DECLINED', 'REJECTED'].includes(statusUpper);
  });

  const willingnessLocationGroups = (() => {
    const groups: { [locName: string]: any[] } = {};
    willingnessList.forEach((wd: any) => {
      const loc = (wd.flatDetails?.location || wd.location_name || wd.location || "Other Location").trim();
      if (!groups[loc]) {
        groups[loc] = [];
      }
      groups[loc].push(wd);
    });
    return groups;
  })();

  const willingnessLocationNames = Object.keys(willingnessLocationGroups);

  const displayedWillingnessOffers =
    selectedWillingnessLocTab === "ALL"
      ? willingnessList
      : willingnessLocationGroups[selectedWillingnessLocTab] || [];

  const showUnwillingButton =
    ['PENDING', 'APPROVED'].includes((data.status || '').toUpperCase()) &&
    !hasAllotmentData &&
    !hasDeclinedWillingness;

  const handleUnwillingClick = () => {
    const targetWillingness = pendingWillingness || willingnessList[0];
    Modal.confirm({
      title: (
        <div className="d-flex align-items-center gap-2">
          <i className="ti ti-alert-triangle text-danger fs-3"></i>
          <span className="fw-bold text-dark fs-5">
            Confirm Unwillingness
          </span>
        </div>
      ),
      icon: null,
      width: 450,
      content: (
        <div className="pt-2">
          <p className="mb-3 text-secondary" style={{ fontSize: '14px', lineHeight: '1.6' }}>
            Are you sure you want to declare yourself unwilling for this staff quarter request? This action cannot be undone.
          </p>
          <div className="mb-2 text-muted small fw-semibold">Remarks (Optional)</div>
          <Input.TextArea
            id="unwilling-remarks"
            rows={3}
            placeholder="Enter your remarks here..."
            maxLength={200}
            className="form-control"
            style={{ borderRadius: '8px', borderColor: '#d9d9d9' }}
          />
        </div>
      ),
      okText: 'Yes, Unwilling',
      okButtonProps: {
        style: {
          backgroundColor: '#dc3545',
          color: '#fff',
          borderColor: '#dc3545',
          borderRadius: '20px',
          height: '38px',
          padding: '0 20px',
          fontWeight: '600',
        }
      },
      cancelText: 'Cancel',
      cancelButtonProps: {
        style: {
          backgroundColor: '#f8f9fa',
          color: '#5f6c7d',
          borderColor: '#d9d9d9',
          borderRadius: '20px',
          height: '38px',
          padding: '0 20px',
          fontWeight: '600',
        }
      },
      onOk: () => {
        const remarksEl = document.getElementById("unwilling-remarks") as HTMLTextAreaElement;
        const remarksVal = remarksEl?.value || "";

        if (targetWillingness) {
          const payload = {
            willingnessReferenceNumber: targetWillingness.referenceNumber,
            isWilling: false,
            remarks: remarksVal || "",
          };
          dispatch(confirmStaffQuarterWillingness(payload));
        } else {
          const payload = {
            registrationReferenceNumber: data.referenceNumber,
            reason: remarksVal || "",
            remarks: remarksVal || "",
          };
          dispatch(withdrawStaffQuarterOccupation(payload));
        }
      }
    });
  };



  const formatDate = (date: any, includeTime: boolean = false) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();

    const dateStr = `${day} ${month}, ${year}`;

    if (includeTime) {
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return (
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: '11px' }}>{dateStr}</span>
          <span className="text-muted extra-small d-flex align-items-center" style={{ fontSize: '11px', borderLeft: '1px solid #dee2e6', paddingLeft: '8px' }}>
            <i className="ti ti-clock me-1" style={{ fontSize: '10px' }}></i>{hours}:{minutes}
          </span>
        </div>
      );
    }

    return dateStr;
  };

  const statusMeta = (status: string) => {
    const s = (status ?? '').toUpperCase().trim();

    const getMeta = (color: string, bg: string, icon: string) => ({ color, bg, icon });

    // Exact matches first
    switch (s) {
      case 'APPROVED': return getMeta('#22c55e', '#dcfce7', 'ti ti-check');
      case 'REJECTED': return getMeta('#ef4444', '#fee2e2', 'ti ti-x');
      case 'PENDING': return getMeta('#f59e0b', '#fef3c7', 'ti ti-clock');
    }

    // Match by keyword
    if (/REJECT|DECLIN|CANCEL/.test(s)) return getMeta('#ef4444', '#fee2e2', 'ti ti-x');
    if (/APPROV|CONFIRM|ALLOT|COMPLET|SUCCESS/.test(s)) return getMeta('#22c55e', '#dcfce7', 'ti ti-check');
    if (/PENDING|WAIT|PROGRESS|REVIEW/.test(s)) return getMeta('#f59e0b', '#fef3c7', 'ti ti-clock');

    return getMeta('#64748b', '#f1f5f9', 'ti ti-info-circle');
  };

  const activeWdIdx = parseInt(activeWillingnessTabKey || "0", 10);
  const activeWd = willingnessList[activeWdIdx] || activeWillingnessDetail || willingnessList[0];
  const targetWdForTracker = approvedWillingness || activeWd || willingnessList[0];

  const activeWillingnessApprovers = (() => {
    const targetWd = targetWdForTracker;
    if (!targetWd) return [];
    const approversSource =
      targetWd.approvers ||
      targetWd.willingnessApprovers ||
      targetWd.approvals ||
      targetWd.approvalDetails ||
      targetWd.approverDetails ||
      targetWd.willingness_approvers ||
      targetWd.willingnessApproverDetails ||
      data.willingnessApprovers ||
      data.willingness_approvers ||
      data.willingnessApproverDetails ||
      data.willingnessApprovals ||
      data.approverDetails ||
      [];

    const isGlobal = (
      approversSource === data.willingnessApprovers ||
      approversSource === data.willingness_approvers ||
      approversSource === data.willingnessApproverDetails ||
      approversSource === data.willingnessApprovals ||
      approversSource === data.approverDetails
    );

    const targetIdStr = String(targetWd.id || targetWd.willingnessId || "").trim();
    const targetRefStr = String(targetWd.referenceNumber || targetWd.willingnessReferenceNumber || targetWd.offerRef || "").trim();

    return (Array.isArray(approversSource) ? approversSource : []).filter((appr: any) => {
      if (!isGlobal) return true;

      const apprWillingnessId = String(appr.willingnessId || appr.willingness_id || appr.targetId || "").trim();
      const apprRef = String(appr.referenceNumber || appr.willingnessReferenceNumber || appr.offerRef || appr.willingness_reference_number || "").trim();

      if (apprWillingnessId && targetIdStr) return apprWillingnessId === targetIdStr;
      if (apprRef && targetRefStr) return apprRef === targetRefStr;
      if (!apprWillingnessId && !apprRef) {
        return willingnessList.indexOf(targetWd) === 0 || willingnessList.length === 1;
      }
      return false;
    });
  })();

  const meta = statusMeta(data.mainStatus || data.status);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Application Status</h3>
            <small className="text-muted">
              Reference Number: {data.referenceNumber}
            </small>
          </div>
          <nav>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to={routes.employeeDashboard}>Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={routes.staffQuarter.list}>Staff Quarters</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Details
              </li>
            </ol>
          </nav>
        </div>

        {((activeTabKey !== "occupation" && activeTabKey !== "willingness" && activeTabKey !== "finance") ||
          (activeTabKey === "willingness" && activeWd) ||
          (activeTabKey === "occupation" && data.occupationDetails) ||
          (activeTabKey === "finance" && financeObj)) && (
            <div className="flex-grow-1 pb-2 pt-2">
              <ApproverTracker
                approvers={(() => {
                  if (activeTabKey === "willingness") {
                    const targetWd = targetWdForTracker;
                    if (!targetWd) return [];
                    return [
                      {
                        id: (data.employeeNumber || "0") + "_willing",
                        name: targetWd?.referenceNumber || data.employeeName || "User",
                        status: (() => {
                          const wStatus = (targetWd.willingnessStatus || "").toUpperCase();
                          const isWilling = targetWd.isWilling === "YES" || wStatus === "CONFIRMED" || wStatus === "APPROVED" || wStatus === "ALLOTTED" || wStatus === "ALLOTED";
                          const isNotWilling = targetWd.isWilling === "NO" || wStatus === "DECLINED";
                          if (isWilling) return "Confirmed";
                          if (isNotWilling) return "Declined";
                          return "Pending";
                        })(),
                        stepName: "Willingness",
                        avatar: (data.details?.image || data.image || data.sap_employee_images?.image)
                          ? ((data.details?.image || data.image || data.sap_employee_images?.image).startsWith("data:")
                            ? (data.details?.image || data.image || data.sap_employee_images?.image)
                            : `data:image/jpeg;base64,${data.details?.image || data.image || data.sap_employee_images?.image}`)
                          : undefined,
                      },
                      ...activeWillingnessApprovers.map((appr: any, idx: number) => {
                        const apprStatus = (appr.status || appr.approvalStatus || appr.state || "").toUpperCase();
                        let displayStatus: "Approved" | "Rejected" | "Pending" | "Confirmed" = "Pending";
                        if (["APPROVED", "CONFIRMED", "PASSED", "ACCEPT", "ACCEPTED"].includes(apprStatus)) {
                          displayStatus = "Approved";
                        } else if (["REJECTED", "DECLINED", "REJECT", "CANCEL", "CANCELLED"].includes(apprStatus)) {
                          displayStatus = "Rejected";
                        } else {
                          displayStatus = "Pending";
                        }

                        return {
                          id: (appr.empId || appr.employeeNumber || appr.id || idx) + "_willing_" + idx,
                          name: appr.name || appr.employeeName || appr.empName || appr.approverName || appr.role || appr.designation || "Approver",
                          status: displayStatus,
                          stepName: appr.stepName || appr.step_name || appr.role || appr.designation || appr.approverRole || undefined,
                          avatar: (appr.image || appr.avatar || appr.employeeImage || appr.profileImage)
                            ? ((appr.image || appr.avatar || appr.employeeImage || appr.profileImage).startsWith("data:")
                              ? (appr.image || appr.avatar || appr.employeeImage || appr.profileImage)
                              : `data:image/jpeg;base64,${appr.image || appr.avatar || appr.employeeImage || appr.profileImage}`)
                            : undefined,
                        };
                      })
                    ];
                  }

                  if (activeTabKey === "occupation") {
                    return data.occupationDetails ? [
                      {
                        id: (data.employeeNumber || data.empId || "0") + "_occupation",
                        name: data.employeeName || "User",
                        status: data.occupationDetails?.status === "APPROVED" ? "Approved" : "Submitted",
                        stepName: "Occupation",
                        avatar: (data.details?.image || data.image || data.sap_employee_images?.image)
                          ? ((data.details?.image || data.image || data.sap_employee_images?.image).startsWith("data:")
                            ? (data.details?.image || data.image || data.sap_employee_images?.image)
                            : `data:image/jpeg;base64,${data.details?.image || data.image || data.sap_employee_images?.image}`)
                          : undefined,
                      },
                      ...(data.occupationDetails?.approvals || []).map((appr: any) => {
                        const apprStatus = (appr.status || "").toUpperCase();
                        let displayStatus: "Approved" | "Rejected" | "Pending" = "Pending";
                        if (apprStatus === "APPROVED") {
                          displayStatus = "Approved";
                        } else if (apprStatus === "REJECTED" || apprStatus === "DECLINED") {
                          displayStatus = "Rejected";
                        } else {
                          displayStatus = "Pending";
                        }

                        return {
                          id: appr.id + "_occupation",
                          name: appr.name || appr.employeeName || "Approver",
                          status: displayStatus,
                          avatar: appr.image ? (appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`) : undefined,
                        };
                      })
                    ] : [];
                  }

                  if (activeTabKey === "finance") {
                    return financeObj ? [
                      {
                        id: (data.employeeNumber || data.empId || "0") + "_finance",
                        name: data.employeeName || "User",
                        status: (financeObj?.status || "").toUpperCase() === "APPROVED" ? "Approved" : "Submitted",
                        stepName: "Finance",
                        avatar: (data.details?.image || data.image || data.sap_employee_images?.image)
                          ? ((data.details?.image || data.image || data.sap_employee_images?.image).startsWith("data:")
                            ? (data.details?.image || data.image || data.sap_employee_images?.image)
                            : `data:image/jpeg;base64,${data.details?.image || data.image || data.sap_employee_images?.image}`)
                          : undefined,
                      },
                      ...financeApprovers.map((appr: any, idx: number) => ({
                        id: (appr.id || appr.empId || idx) + "_finance",
                        name: appr.name,
                        status: financeObj?.status === "APPROVED" ? "Approved" : (appr.status ? (appr.status.charAt(0).toUpperCase() + appr.status.slice(1).toLowerCase()) : "Pending"),
                        avatar: appr.image ? (appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`) : undefined,
                      }))
                    ] : [];
                  }

                  // Default to Registration
                  return [
                    {
                      id: data.employeeNumber || "0",
                      name: data.employeeName || "User",
                      status: "Submitted",
                      stepName: "Registration",
                      avatar: (data.details?.image || data.image || data.sap_employee_images?.image)
                        ? ((data.details?.image || data.image || data.sap_employee_images?.image).startsWith("data:")
                          ? (data.details?.image || data.image || data.sap_employee_images?.image)
                          : `data:image/jpeg;base64,${data.details?.image || data.image || data.sap_employee_images?.image}`)
                        : undefined,
                    },
                    ...(data.approverDetails || []).map((appr: any) => ({
                      id: appr.empId,
                      name: appr.name,
                      status: data.status === "APPROVED" ? "Approved" : (appr.status ? (appr.status.charAt(0).toUpperCase() + appr.status.slice(1).toLowerCase()) : "Pending"),
                      avatar: appr.image ? (appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`) : undefined,
                    }))
                  ];
                })()}
              />
            </div>
          )}

        {/* <div className="card mb-3">
          <div className="card-body">
            <div
              className="d-flex align-items-center p-3 rounded"
              style={{ background: meta.bg }}
            >
              <div className="me-3">
                <i
                  className={`${meta.icon} fs-3`}
                  style={{ color: meta.color }}
                />
              </div>
              <div className="flex-grow-1">
                <div className="small text-muted">Current Status</div>
                <h3 style={{ margin: 0, color: meta.color, fontWeight: 800 }}>{data.mainStatus}</h3>
              </div>
              <div>
              </div>
            </div>
          </div>
        </div> */}

        <div className="tabs-wrapper mb-2">
          <Tabs
            type="card"
            activeKey={activeTabKey}
            onChange={(key) => setActiveTabKey(key)}
            items={[
              {
                key: "registration",
                label: (
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-file-description" style={{ fontSize: '18px' }}></i>
                    <span className="fw-semibold">Registration</span>
                  </div>
                ),
                children: (
                  <div className="d-flex flex-column gap-3 pt-2">
                    {/* Employee Information */}
                    <div className="card mb-3">
                      <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Employee Information</h5>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded bg-light">
                          <div className="avatar avatar-xxl rounded-circle overflow-hidden border shadow-sm" style={{ width: '80px', height: '80px' }}>
                            {(data.details?.image || data.image || data.sap_employee_images?.image) ? (
                              <img
                                src={(data.details?.image || data.image || data.sap_employee_images?.image).startsWith("data:")
                                  ? (data.details?.image || data.image || data.sap_employee_images?.image)
                                  : `data:image/jpeg;base64,${data.details?.image || data.image || data.sap_employee_images?.image}`}
                                alt="Employee"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="h-100 w-100 d-flex align-items-center justify-content-center bg-white">
                                <i className="ti ti-user text-muted fs-1"></i>
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="mb-1 fw-bold text-dark">{data.employeeName || "Employee"}</h4>
                            <p className="mb-0 text-muted">
                              <span className="badge bg-primary-light text-primary me-2">{data.empId || "ID-N/A"}</span>
                              {data.designation?.name || "Designation N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Employee Name</div>
                            <div className="fw-bold text-dark">{data.employeeName || "-"}</div>
                          </div>
                          <div className="col-md-2 mb-3">
                            <div className="text-muted small">Employee ID</div>
                            <div className="fw-bold text-dark">{data.empId || "-"}</div>
                          </div>
                          <div className="col-md-2 mb-3">
                            <div className="text-muted small">Designation</div>
                            <div className="fw-bold text-dark">{data.designation?.name || "-"}</div>
                          </div>
                          <div className="col-md-2 mb-3">
                            <div className="text-muted small">Department</div>
                            <div className="fw-bold text-dark">{data.department?.name || "-"}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Payscale</div>
                            <div className="fw-bold text-dark">{data.payScale || "-"}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Basic Pay</div>
                            <div className="fw-bold text-dark">{data.basicPay ? `₹${data.basicPay}` : "-"}</div>
                          </div>

                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Date of Birth</div>
                            <div className="fw-bold text-dark">{formatDate(data.dateOfBirth)}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Date of Joining</div>
                            <div className="fw-bold text-dark">{formatDate(data.dateOfJoining)}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Date of Retirement</div>
                            <div className="fw-bold text-dark">{formatDate(data.dateOfRetirement)}</div>
                          </div>

                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Category</div>
                            <div className="fw-bold text-dark">{data.category || "-"}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Employment Type</div>
                            <div className="fw-bold text-dark">{data.employmentType || "-"}</div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Employee Group</div>
                            <div className="fw-bold text-dark">{data.employeeGroup || "-"}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="card mb-3">
                      <div className="card-header bg-white border-bottom">
                        <h5 className="mb-0">Contact Information</h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Mobile Number</div>
                            <div className="fw-bold text-dark">{data.contact?.mobileNumber || "-"}</div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Email Address</div>
                            <div className="fw-bold text-dark">{data.contact?.email || "-"}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Withdrawal Details */}
                    {data.status === 'WITHDRAWN' && (
                      <div className="card mb-4 border-0 shadow-none bg-light-subtle" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div className="card-header bg-white border-bottom py-3 d-flex align-items-center">
                          <i className="ti ti-alert-circle text-warning fs-2 me-2"></i>
                          <h5 className="mb-0 text-dark small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                            Withdrawal Details
                          </h5>
                        </div>
                        <div className="card-body p-3">
                          <div className="bg-light p-3 rounded border border-warning-subtle">
                            <div className="row">
                              <div className="col-12">
                                <div className="text-muted small fw-semibold mb-1">Reason for Withdrawal</div>
                                <div className="fw-bold text-dark" style={{ wordBreak: 'break-word', fontSize: '14px' }}>
                                  {data.withdrawalDetails?.reason || data.reason || data.withdrawalReason || "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Family Details */}
                    {((data?.familyDetails?.length > 0) || (data?.occupationDetails?.familyDetails?.length > 0)) && (
                      <div className="card mb-3">
                        <div className="card-header bg-white border-bottom">
                          <h5 className="mb-0 text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                            Family Details
                          </h5>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-hover table-center mb-0">
                              <thead className="thead-light">
                                <tr>
                                  <th>Name</th>
                                  <th>Relationship</th>
                                  <th>Photo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(data?.familyDetails || data?.occupationDetails?.familyDetails || []).map((member: any, idx: number) => (
                                  <tr key={idx}>
                                    <td className="fw-bold">{member.memberName || member.name || "-"}</td>
                                    <td>{member.relationship || "-"}</td>
                                    <td>
                                      {(member.documentUrl || member.image) ? (
                                        <a
                                          href={(member.documentUrl || member.image).startsWith("data:") || (member.documentUrl || member.image).startsWith("http") ? (member.documentUrl || member.image) : `data:image/jpeg;base64,${member.documentUrl || member.image}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-primary text-decoration-none"
                                        >
                                          <i className="ti ti-paperclip me-1"></i>
                                          Attachment {idx + 1}
                                        </a>
                                      ) : <span className="text-muted small">No Attachment</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Application Details */}
                    <div className="card mb-3">
                      <div className="card-header bg-white border-bottom">
                        <h5 className="mb-0">Application Details</h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Application ID</div>
                            <div className="fw-bold text-dark">{data.referenceNumber || "-"}</div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Registration Status</div>
                            <div className="mt-1">
                              {(() => {
                                const meta = statusMeta(data.status);
                                return (
                                  <span
                                    className="badge px-3 py-2"
                                    style={{
                                      backgroundColor: meta.bg,
                                      color: meta.color,
                                      border: `1px solid ${meta.color}33`,
                                      fontWeight: '600'
                                    }}
                                  >
                                    <i className={`${meta.icon} me-1`}></i>
                                    {data.status || "-"}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Pool Name</div>
                            <div className="fw-bold text-dark">{data.pool?.name || "-"}</div>
                          </div>

                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Eligibility Type</div>
                            <div className="fw-bold text-dark">
                              {data.quarterTypeEligibility?.type
                                ? data.quarterTypeEligibility.type.replace(/\s*accommodation\s*/gi, "")
                                : (data.quarterTypeEligibility?.quarterType || "-")}
                            </div>
                          </div>
                          <div className="col-md-4 mb-3">
                            <div className="text-muted small">Spouse Allotted In Delhi/NCR</div>
                            <div className="fw-bold text-dark">{data.isSpouseAllottedInDelhiNcr ? "Yes" : "No"}</div>
                          </div>

                          {(() => {
                            const validPrefs = (data.locationPreferences || []).filter((l: any) => l.name && l.name.trim() !== "" && l.name !== "-").sort((a: any, b: any) => a.preference - b.preference);
                            // if (validPrefs.length === 0) return null;
                            return (
                              <div className="col-12 mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <div className="text-muted small">Location Preferences</div>
                                  {(data.status || '').toUpperCase() === 'APPROVED' && (!data.allocatedLocation || data.allocatedLocation === '-') && (
                                    <button
                                      onClick={() => setShowLocationModal(true)}
                                      className="btn btn-sm btn-outline-primary py-0 px-2 d-inline-flex align-items-center gap-1"
                                      style={{ fontSize: '11px', height: '22px' }}
                                    >
                                      <i className="ti ti-edit"></i> Edit Preferences
                                    </button>
                                  )}
                                </div>
                                <div className="d-flex gap-2 flex-wrap mt-1">
                                  {validPrefs.length > 0 ? (
                                    validPrefs.map((l: any) => (
                                      <span key={l.id || l.preference} className="badge bg-light border text-dark px-3 py-2">
                                        {l.preference}. {l.name}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-muted small">No preferences set</span>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {data.allocatedLocation && data.allocatedLocation !== "-" && (
                            <div className="col-md-6 mb-3">
                              <div className="text-muted small">Allocated Location</div>
                              <div className="text-success fw-bold">
                                {data.allocatedLocation}
                              </div>
                            </div>
                          )}
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Application Date</div>
                            <div className="fw-bold text-dark">{formatDate(data.createdAt)}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Registration Workflow Remarks */}
                    {(data.approverDetails || []).some((appr: any) => appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null") && (
                      <div className="mb-4">
                        <div className="text-muted small mb-3 fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Registration Workflow & Remarks</div>
                        <div className="mt-2">
                          {(data.approverDetails || []).filter((appr: any) => appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null").map((appr: any, index: number) => (
                            <div key={index} className="card border-0 mb-2 shadow-none" style={{ borderRadius: '12px', border: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden"
                                      style={{ width: '40px', height: '40px', border: '1px solid #f1f5f9' }}
                                    >
                                      {appr.image ? (
                                        <img
                                          src={appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`}
                                          alt={appr.name}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      ) : (
                                        <i className="ti ti-user text-muted" style={{ fontSize: '18px' }}></i>
                                      )}
                                    </div>
                                    <div>
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>{appr.name}</div>
                                      </div>
                                      <div className="text-muted extra-small" style={{ fontSize: '11px' }}>
                                        {appr.designation ? `${appr.designation} | ` : ""}{appr.empId}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <span
                                      className={`fw-bold text-uppercase ${appr.status?.toUpperCase() === 'APPROVED' ? 'text-success' : 'text-danger'}`}
                                      style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                                    >
                                      {appr.status}
                                    </span>
                                    <div className="text-muted extra-small" style={{ fontSize: '10px' }}>
                                      {formatDate(appr.approvedAt || appr.updatedAt, true)}
                                    </div>
                                  </div>
                                </div>
                                {appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null" && (
                                  <div
                                    className="p-2 px-3 rounded-2"
                                    style={{ backgroundColor: '#f8fafc', borderLeft: `3px solid #0b57b7` }}
                                  >
                                    <div className="d-flex gap-2 align-items-start">
                                      <i className="ti ti-quote text-muted" style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                      <div className="text-secondary" style={{ fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                        {appr.remarks}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
              ...(hasWillingnessData ? [{
                key: "willingness",
                label: (
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-mail" style={{ fontSize: '18px' }}></i>
                    <span className="fw-semibold">Willingness</span>
                  </div>
                ),
                children: (
                  <div className="d-flex flex-column gap-3 pt-2">
                    {/* Willingness Opportunities (Location-Wise) */}
                    {willingnessList.length > 0 ? (
                      <div className="card mb-3 shadow-sm border-0" style={{ borderRadius: "12px" }}>
                        <div className="card-header bg-white border-bottom py-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle text-primary"
                              style={{ width: "32px", height: "32px", background: "rgba(var(--bs-primary-rgb), 0.08)" }}
                            >
                              <i className="ti ti-building-community" style={{ fontSize: "18px" }}></i>
                            </div>
                            <div>
                              <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                                Willingness Opportunities (Location-Wise)
                              </h5>
                              <span className="text-muted extra-small" style={{ fontSize: "11px" }}>
                                Showing quarter allotment willingness opportunities organized by location
                              </span>
                            </div>
                          </div>
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1 fw-semibold">
                            Total Opportunities: {willingnessList.length}
                          </span>
                        </div>
                        <div className="card-body">
                          {/* Location filter tabs */}
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-4 pb-2 border-bottom">
                            <button
                              type="button"
                              className={`btn btn-sm rounded-pill px-3 fw-semibold ${selectedWillingnessLocTab === "ALL" ? "btn-primary shadow-xs" : "btn-light border text-muted"}`}
                              onClick={() => setSelectedWillingnessLocTab("ALL")}
                            >
                              <i className="ti ti-layout-grid me-1"></i>
                              All Locations ({willingnessList.length})
                            </button>
                            {willingnessLocationNames.map((loc) => (
                              <button
                                key={loc}
                                type="button"
                                className={`btn btn-sm rounded-pill px-3 fw-semibold ${selectedWillingnessLocTab === loc ? "btn-primary shadow-xs" : "btn-light border text-muted"}`}
                                onClick={() => setSelectedWillingnessLocTab(loc)}
                              >
                                <i className="ti ti-map-pin me-1 text-danger"></i>
                                {loc} ({willingnessLocationGroups[loc]?.length || 0})
                              </button>
                            ))}
                          </div>

                          {/* Location-wise Grouped Cards */}
                          <div className="d-flex flex-column gap-3">
                            {displayedWillingnessOffers.map((wd: any, idx: number) => {
                              const locName = wd.flatDetails?.location || wd.location_name || wd.location || "Location";
                              const flatNoFormatted = formatFlatNoWithBlock(wd, wd.block || data?.block, data);
                              const quarterType = wd.flatDetails?.quarterType || wd.quarterType || "-";
                              const poolName = wd.flatDetails?.poolType || wd.pool_name || wd.poolName || "-";
                              const categoryName = resolveFlatCategory(wd, wd.block || data?.block, data);
                              const wStatus = (wd.willingnessStatus || "").toUpperCase();
                              const isWSuccess = wStatus === 'CONFIRMED' || wStatus === 'APPROVED' || wStatus === 'ALLOTTED' || wStatus === 'ALLOTED';
                              const isWError = wStatus === 'REJECTED' || wStatus === 'DECLINED' || wStatus === 'UNWILLING';

                              return (
                                <div
                                  key={idx}
                                  className="card border shadow-xs mb-0 overflow-hidden"
                                  style={{ borderRadius: "12px", borderColor: "#e2e8f0" }}
                                >
                                  <div
                                    className="card-header bg-light py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
                                    style={{ borderBottom: "1px solid #edf2f7" }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-danger rounded-pill px-2.5 py-1 d-inline-flex align-items-center gap-1">
                                        <i className="ti ti-map-pin" style={{ fontSize: "12px" }}></i>
                                        {locName}
                                      </span>
                                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                        Offer Ref: <span className="text-primary">{wd.referenceNumber || `Offer #${idx + 1}`}</span>
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className={`badge ${
                                          isWSuccess
                                            ? "bg-success-subtle text-success border border-success-subtle"
                                            : isWError
                                            ? "bg-danger-subtle text-danger border border-danger-subtle"
                                            : "bg-warning-subtle text-warning border border-warning-subtle"
                                        } px-2.5 py-1 fw-semibold d-inline-flex align-items-center gap-1`}
                                      >
                                        <i className={`ti ${isWSuccess ? "ti-circle-check" : isWError ? "ti-circle-x" : "ti-clock"}`}></i>
                                        {wd.willingnessStatus || "PENDING"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="card-body p-3 bg-white">
                                    <div className="row g-3 align-items-center">
                                      <div className="col-lg-3 col-md-6">
                                        <div className="text-muted small fw-medium">Flat No</div>
                                        <div className="fw-bold text-dark mt-0.5">
                                          {flatNoFormatted}
                                        </div>
                                      </div>
                                      <div className="col-lg-3 col-md-6">
                                        <div className="text-muted small fw-medium">Location</div>
                                        <div className="fw-bold text-dark mt-0.5">
                                          <i className="ti ti-map-pin text-danger me-1"></i>
                                          {locName}
                                        </div>
                                      </div>
                                      <div className="col-lg-3 col-md-6">
                                        <div className="text-muted small fw-medium">Quarter Type & Pool</div>
                                        <div className="fw-bold text-dark mt-0.5">
                                          {quarterType} <span className="text-muted">({poolName})</span>
                                        </div>
                                      </div>
                                      <div className="col-lg-3 col-md-6">
                                        <div className="text-muted small fw-medium">Category</div>
                                        <div className="fw-bold text-dark mt-0.5">
                                          {categoryName}
                                        </div>
                                      </div>

                                      <div className="col-lg-3 col-md-6 border-top pt-2">
                                        <div className="text-muted small fw-medium">Offer / Generation Date</div>
                                        <div className="fw-bold text-dark mt-0.5">
                                          {formatDate(wd.createdAt || wd.date || data.createdAt)}
                                        </div>
                                      </div>
                                      <div className="col-lg-9 col-md-6 border-top pt-2">
                                        <div className="text-muted small fw-medium">Willingness Status</div>
                                        <div className="mt-1 d-flex flex-wrap align-items-center gap-2">
                                          <span className={`badge ${isWSuccess ? 'bg-success' : isWError ? 'bg-danger' : 'bg-warning'}`}>
                                            {wd.willingnessStatus || 'PENDING'}
                                          </span>
                                          <span className="text-muted small">({wd.referenceNumber})</span>
                                          {canViewWillingness && wd.willingnessStatus === "PENDING" && (
                                            <button
                                              className="btn btn-success btn-xs rounded-pill px-3 py-1 ms-2"
                                              style={{ fontSize: '11px', padding: '2px 10px', height: 'auto', border: 'none' }}
                                              onClick={() => {
                                                form.resetFields();
                                                setSelectedWillingness("YES");
                                                setSelectedWillingnessRef(wd.referenceNumber);
                                                setIsModalOpen(true);
                                              }}
                                            >
                                              <i className="ti ti-send me-1" style={{ fontSize: '10px' }} />
                                              Confirm / Decline
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Remarks */}
                                      {(wd.remark || wd.remarks) && String(wd.remark || wd.remarks).trim() !== "" && String(wd.remark || wd.remarks).toLowerCase() !== "null" && (
                                        <div className="col-12 border-top pt-2">
                                          <div className="text-muted small fw-medium mb-1">Remarks:</div>
                                          <div className="p-2 px-3 bg-light-subtle rounded border small text-dark" style={{ wordBreak: 'break-word' }}>
                                            {wd.remark || wd.remarks}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="card shadow-sm border-0 mb-0" style={{ borderRadius: "12px" }}>
                        <div className="card-body text-center py-5 text-muted">
                          <i className="ti ti-mail-off fs-1 d-block mb-2 text-secondary opacity-50"></i>
                          <h6 className="fw-bold text-dark mb-1">No Willingness Opportunities Found</h6>
                          <p className="small text-muted mb-0">
                            Offers are generated sequentially according to your priority rank. Once an offer is generated, its willingness details will appear here.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Willingness Approver Remarks */}
                    {(() => {
                      const offersWithRemarks = displayedWillingnessOffers.filter((wd: any) => {
                        const approversSource = wd.approvers || wd.willingnessApprovers || data.willingnessApprovers || [];
                        const isGlobal = approversSource === data.willingnessApprovers;
                        return approversSource.some((appr: any) => {
                          const matchesWillingness = isGlobal ? (
                            (appr.willingnessId && (String(appr.willingnessId) === String(wd.id || wd.willingnessId) || String(appr.willingnessId) === String(activeWillingnessDetail?.id || activeWillingnessDetail?.willingnessId))) ||
                            (appr.referenceNumber && String(appr.referenceNumber) === String(wd.referenceNumber)) ||
                            (!appr.willingnessId && !appr.referenceNumber && willingnessList.indexOf(wd) === 0)
                          ) : true;
                          return matchesWillingness && appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null";
                        });
                      });

                      if (offersWithRemarks.length === 0) return null;

                      return (
                        <div className="card mb-4 border-0 shadow-none bg-light-subtle" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                          <div className="card-header bg-white border-bottom py-3">
                            <h5 className="mb-0 text-dark small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                              Willingness Approver Remarks
                            </h5>
                          </div>
                          <div className="card-body p-0">
                            {offersWithRemarks.map((wd: any, wIdx: number) => {
                              const approversSource = wd.approvers || wd.willingnessApprovers || data.willingnessApprovers || [];
                              const isGlobal = approversSource === data.willingnessApprovers;
                              const filteredApprovers = approversSource.filter((appr: any) => {
                                const matchesWillingness = isGlobal ? (
                                  (appr.willingnessId && (String(appr.willingnessId) === String(wd.id || wd.willingnessId) || String(appr.willingnessId) === String(activeWillingnessDetail?.id || activeWillingnessDetail?.willingnessId))) ||
                                  (appr.referenceNumber && String(appr.referenceNumber) === String(wd.referenceNumber)) ||
                                  (!appr.willingnessId && !appr.referenceNumber && willingnessList.indexOf(wd) === 0)
                                ) : true;
                                return matchesWillingness && appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null";
                              });

                              return (
                                <div key={wIdx} className="p-3 bg-white border-bottom">
                                  <div className="mb-3 px-1">
                                    <span className="text-muted small fw-semibold">
                                      Approver Remarks for <span className="text-primary fw-bold">{wd.referenceNumber || `Offer #${wIdx + 1}`}</span>
                                      {wd.flatDetails?.location ? ` (${wd.flatDetails.location})` : ""}
                                    </span>
                                  </div>
                                  {filteredApprovers.map((appr: any, index: number) => (
                                    <div key={index} className="card border-0 mb-2 shadow-none" style={{ borderRadius: '10px', border: '1px solid #f1f5f9', backgroundColor: '#fcfdfe' }}>
                                      <div className="card-body p-2 px-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div className="d-flex align-items-center">
                                            <div
                                              className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden shadow-sm"
                                              style={{ width: '38px', height: '38px' }}
                                            >
                                              {appr.image ? (
                                                <img
                                                  src={appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`}
                                                  alt={appr.name}
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                              ) : (
                                                <i className="ti ti-user text-muted" style={{ fontSize: '16px' }}></i>
                                              )}
                                            </div>
                                            <div>
                                              <div className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>{appr.name}</div>
                                              <div className="text-muted extra-small" style={{ fontSize: '11px' }}>
                                                {appr.approvedAt ? formatDate(appr.approvedAt, true) : "Awaiting response"}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-end">
                                            <span
                                              className={`badge ${appr.status?.toUpperCase() === 'APPROVED'
                                                ? 'bg-success-light text-success'
                                                : (appr.status?.toUpperCase() === 'PENDING' || !appr.status)
                                                  ? 'bg-warning-light text-warning'
                                                  : 'bg-danger-light text-danger'
                                                }`}
                                              style={{ fontSize: '10px' }}
                                            >
                                              {appr.status || "PENDING"}
                                            </span>
                                          </div>
                                        </div>
                                        {appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null" && (
                                          <div
                                            className="p-2 px-3 rounded-2"
                                            style={{ backgroundColor: '#fff', border: '1px solid #edf2f7', borderLeft: '3px solid #38a169' }}
                                          >
                                            <div className="d-flex gap-2 align-items-start">
                                              <i className="ti ti-quote text-success opacity-50" style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                              <div className="text-secondary" style={{ fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                                {appr.remarks}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Willingness Confirmation Banners */}
                    {/* {canViewWillingness && willingnessList.map((wd: any, idx: number) => {
                      if (wd.willingnessStatus !== "PENDING") return null;
                      return (
                        <div
                          key={idx}
                          className="card mb-4 shadow-sm"
                          style={{
                            border: "2px dashed #28a745",
                            background: "#f8fff9",
                            borderRadius: 12,
                          }}
                        >
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{
                                  width: 52,
                                  height: 52,
                                  background: "#e9fbf0",
                                }}
                              >
                                <i className="ti ti-checkbox fs-3 text-success" />
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="fw-bold mb-1" style={{ color: "#006a3d" }}>
                                  Quarter Allotment — Willingness Confirmation Required
                                </h6>
                                <p className="text-muted small mb-0">
                                  You have been shortlisted for a{" "}
                                  <strong>{wd.quarterType}</strong>{" "}
                                  quarter allotment at{" "}
                                  <strong>{wd.location_name || wd.location}</strong>,{" "}
                                  Block <strong>{wd.block || wd.flatDetails?.block || data?.block || "-"}</strong>,{" "}
                                  Flat <strong>{formatFlatNoWithBlock(wd, wd.block || data?.block, data)}</strong>{" "}
                                  (ID: <strong>{wd.flatId || wd.flatDetails?.id || "-"}</strong>),{" "}
                                  Pool: <strong>{wd.pool_name || wd.poolName}</strong>,{" "}
                                  Category: <strong>{resolveFlatCategory(wd, wd.block || data?.block, data)}</strong>&nbsp;(
                                  <span className="fw-bold" style={{ color: "#006a3d" }}>
                                    {wd.referenceNumber}
                                  </span>
                                  ). Please confirm your willingness to proceed.
                                </p>
                              </div>
                              <button
                                className="btn btn-success rounded-pill px-4 flex-shrink-0"
                                onClick={() => {
                                  form.resetFields();
                                  setSelectedWillingness("YES");
                                  setSelectedWillingnessRef(wd.referenceNumber);
                                  setIsModalOpen(true);
                                }}
                              >
                                <i className="ti ti-send me-1" />
                                Confirm Willingness
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })} */}
                  </div>
                )
              }] : []),
              ...(hasOccupationData ? [{
                key: "occupation",
                label: (
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-home" style={{ fontSize: '18px' }}></i>
                    <span className="fw-semibold">Allotment / Occupation</span>
                  </div>
                ),
                children: (
                  <div className="d-flex flex-column gap-3 pt-2">
                    {/* Allotment Details Section */}
                    <div className="card mb-4 border-0 shadow-none bg-light-subtle" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div
                        className="card-header bg-white py-3 d-flex align-items-center justify-content-between"
                        style={{ borderBottom: '1px solid #f1f5f9' }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex align-items-center justify-content-center rounded-circle text-danger" style={{ width: '32px', height: '32px', background: 'rgba(220, 53, 69, 0.08)' }}>
                            <i className="ti ti-building-community" style={{ fontSize: '18px' }}></i>
                          </div>
                          <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>Allotment Details</h5>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {data.allotmentDetails ? (
                            <span className="badge bg-success">{formatFlatNoWithBlock(data.allotmentDetails, data.block, data) || "Allotted"}</span>
                          ) : (
                            <span className="badge bg-secondary">Awaiting Allotment</span>
                          )}
                          {data.allotmentDetails && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 shadow-sm"
                              onClick={handleDownloadAllotmentLetter}
                              disabled={downloadingLetter}
                              style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px' }}
                            >
                              {downloadingLetter ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="ti ti-download me-1"></i>
                              )}
                              {downloadingLetter ? "Downloading..." : "Allotment Letter"}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="card-body p-3">
                        {/* Allotment Details Card if allotted */}
                        {data.allotmentDetails ? (
                          <div className="p-3 bg-light-subtle rounded-3 border mb-0" style={{ borderColor: '#f1f5f9' }}>
                            <h6 className="fw-bold text-success mb-3 d-flex align-items-center gap-2">
                              <i className="ti ti-home-check"></i> Flat Allotment Information
                            </h6>
                            {!data.occupationDetails && (
                              <div className="alert alert-warning border-0 mb-3 d-flex align-items-center p-2 px-3" style={{ borderRadius: '8px', background: 'rgba(217, 119, 6, 0.1)', borderLeft: '4px solid #d97706' }}>
                                <i className="ti ti-alert-triangle text-warning me-2" style={{ fontSize: '18px' }}></i>
                                <div className="fw-semibold" style={{ fontSize: '13px', color: '#b45309' }}>
                                  Apply for occupation within 21 days; otherwise, your HRA will be deducted.
                                </div>
                              </div>
                            )}
                            <div className="row g-3">
                              <div className="col-md-4">
                                <div className="text-muted small">Allotment ID</div>
                                <div className="fw-bold text-dark">{data.allotmentDetails.referenceNumber || data.allotmentDetails.allotmentReferenceNumber || "-"}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Flat No.</div>
                                <div className="fw-bold text-dark">{formatFlatNoWithBlock(data.allotmentDetails, data.block, data)}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Location</div>
                                <div className="fw-bold text-dark">{data.allotmentDetails.location || data.allotmentDetails.locationName || "-"}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Category</div>
                                <div className="fw-bold text-dark">
                                  {(Array.isArray(data.willingnessDetails)
                                    ? (data.willingnessDetails.find((w: any) => w?.category || w?.flatCategory)?.category || data.willingnessDetails.find((w: any) => w?.category || w?.flatCategory)?.flatCategory || data.willingnessDetails[0]?.category || data.willingnessDetails[0]?.flatCategory)
                                    : (data.willingnessDetails?.category || data.willingnessDetails?.flatCategory)) || resolveFlatCategory(data.allotmentDetails, data.block, data)}
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Pool</div>
                                <div className="fw-bold text-dark">{data.allotmentDetails.pool || data.allotmentDetails.poolName || data.allotmentDetails.poolType || data.pool || "-"}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Quarter</div>
                                <div className="fw-bold text-dark">{data.allotmentDetails.quarterType || data.allotmentDetails.quarter || "-"}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Allotment Date</div>
                                <div className="fw-bold text-dark">{formatDate(data.allotmentDetails.allotmentDate)}</div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-muted small">Status</div>
                                <div><span className="badge bg-success">{data.allotmentDetails.status || "ALLOTTED"}</span></div>
                              </div>
                            </div>
                            {data.allotmentDetails.notes && (
                              <div className="col-12 border-top pt-2">
                                <div className="text-muted small">Notes / Remarks</div>
                                <div className="fw-bold text-dark mt-1" style={{ wordBreak: 'break-word' }}>{data.allotmentDetails.notes}</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-3 text-muted">
                            <i className="ti ti-info-circle fs-3 mb-2 d-block text-secondary"></i>
                            No allotment has been confirmed yet.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Occupation Details & Remarks */}
                    {data.occupationDetails ? (
                      <div className="card mb-4 border-0 shadow-none bg-light-subtle" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div className="card-header bg-white border-bottom py-3">
                          <h5 className="mb-0 text-dark small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                            Occupation Details & Remarks
                          </h5>
                        </div>
                        <div className="card-body p-3">
                          <div className="bg-light p-3 rounded mb-3 border">
                            <div className="row align-items-center">
                              <div className="col-md-6 mb-2">
                                <div className="text-muted small fw-semibold">Occupation Date</div>
                                <div className="fw-bold text-dark">{formatDate(data.occupationDetails.occupationDate)}</div>
                              </div>
                              <div className="col-md-6 mb-2 text-md-end">
                                <div className="text-muted small fw-semibold">Overall Status</div>
                                <span className={`badge ${data.occupationDetails.status === 'APPROVED' ? 'bg-success' : data.occupationDetails.status === 'REJECTED' ? 'bg-danger' : 'bg-warning'}`}>
                                  {data.occupationDetails.status}
                                </span>
                              </div>
                              {data.occupationDetails.remarks && (
                                <div className="col-12 mt-2">
                                  <div className="text-muted small fw-semibold">Your Submission Remarks</div>
                                  <div className="bg-white p-2 px-3 rounded small border-start border-3 border-info mt-1 shadow-sm" style={{ wordBreak: 'break-word' }}>
                                    {data.occupationDetails.remarks}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {(data.occupationDetails.approvals || []).length > 0 && (
                            <div className="mt-3">
                              <div className="text-muted small mb-3 fw-bold text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Approver Feedback & Timeline</div>
                              {(data.occupationDetails.approvals || []).map((appr: any, index: number) => (
                                <div key={index} className="card border-0 mb-2 shadow-none" style={{ borderRadius: '10px', border: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
                                  <div className="card-body p-2 px-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div className="d-flex align-items-center">
                                        <div
                                          className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden shadow-sm"
                                          style={{ width: '38px', height: '38px' }}
                                        >
                                          {appr.image ? (
                                            <img
                                              src={appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`}
                                              alt={appr.name}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                          ) : (
                                            <i className="ti ti-user text-muted" style={{ fontSize: '16px' }}></i>
                                          )}
                                        </div>
                                        <div>
                                          <div className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>
                                            {appr.name}
                                            {(appr.empId || appr.employeeNumber) && (
                                              <span className="text-muted fw-normal small ms-1" style={{ fontSize: '11px' }}>
                                                ({appr.empId || appr.employeeNumber})
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-muted extra-small d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                                            {appr.status === 'PENDING' ? (
                                              <span>Awaiting response</span>
                                            ) : (
                                              <span>
                                                <i className="ti ti-clock me-1" style={{ fontSize: '10px' }}></i>
                                                {formatDate(appr.approvedAt || appr.updatedAt || appr.createdAt || data?.occupationDetails?.updatedAt || data?.occupationDetails?.occupationDate, true)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-end">
                                        <span
                                          className={`badge ${appr.status?.toUpperCase() === 'APPROVED'
                                            ? 'bg-success-light text-success'
                                            : appr.status?.toUpperCase() === 'REJECTED'
                                              ? 'bg-danger-light text-danger'
                                              : 'bg-warning-light text-warning'
                                            }`}
                                          style={{ fontSize: '10px' }}
                                        >
                                          {appr.status || 'PENDING'}
                                        </span>
                                        {appr.status !== 'PENDING' && (appr.approvedAt || appr.updatedAt || appr.createdAt || data?.occupationDetails?.updatedAt) && (
                                          <div className="text-muted extra-small mt-1" style={{ fontSize: '10.5px' }}>
                                            {formatDate(appr.approvedAt || appr.updatedAt || appr.createdAt || data?.occupationDetails?.updatedAt, true)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null" && (
                                      <div
                                        className="p-2 px-3 rounded-2"
                                        style={{ backgroundColor: '#fcfdfe', border: '1px solid #edf2f7', borderLeft: '3px solid #6366f1' }}
                                      >
                                        <div className="d-flex gap-2 align-items-start">
                                          <i className="ti ti-message-2 text-primary opacity-50" style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                          <div className="text-secondary" style={{ fontSize: '12.5px', lineHeight: '1.5', fontStyle: 'italic', wordBreak: 'break-word' }}>
                                            {appr.remarks}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="card shadow-sm border-0 mb-0">
                        <div className="card-body text-center py-4 text-muted">
                          <i className="ti ti-info-circle fs-3 mb-2 d-block text-secondary"></i>
                          No occupation details available.
                        </div>
                      </div>
                    )}
                  </div>
                )
              }] : []),
              ...(hasFinanceData ? [{
                key: "finance",
                label: (
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-cash" style={{ fontSize: '18px' }}></i>
                    <span className="fw-semibold">Finance</span>
                  </div>
                ),
                children: (
                  <div className="d-flex flex-column gap-3 pt-2">
                    {/* Finance Details Section */}
                    <div className="card shadow-sm border-0 mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <div
                        className="card-header bg-white py-3 d-flex align-items-center justify-content-between"
                        style={{ borderBottom: '1px solid #f1f5f9' }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex align-items-center justify-content-center rounded-circle text-primary" style={{ width: '32px', height: '32px', background: 'rgba(var(--bs-primary-rgb), 0.08)' }}>
                            <i className="ti ti-receipt-tax" style={{ fontSize: '18px' }}></i>
                          </div>
                          <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>Finance Details</h5>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {financeObj?.referenceNumber && (
                            <span className="text-muted small fw-semibold me-2">Ref: {financeObj.referenceNumber}</span>
                          )}
                          <span className={`badge ${(financeObj?.status || "").toUpperCase() === "APPROVED" ? "bg-success" : (financeObj?.status || "").toUpperCase() === "REJECTED" ? "bg-danger" : "bg-warning text-white"}`}>
                            {financeObj?.status || "PENDING"}
                          </span>
                        </div>
                      </div>
                      <div className="card-body">
                        {/* Flat Allotment Information in Finance Tab */}
                        {(data.allotmentDetails || apiData?.allotmentDetails) && (() => {
                          const allot = data.allotmentDetails || apiData?.allotmentDetails || {};
                          return (
                            <div className="p-3 bg-light-subtle rounded-3 border mb-4" style={{ borderColor: '#f1f5f9' }}>
                              <h6 className="fw-bold text-success mb-3 d-flex align-items-center gap-2">
                                <i className="ti ti-home-check"></i> Flat Allotment Information
                              </h6>
                              <div className="row g-3">
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Allotment ID</div>
                                  <div className="fw-bold text-dark">{allot.referenceNumber || allot.allotmentReferenceNumber || data.allotmentReferenceNumber || "-"}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Flat No.</div>
                                  <div className="fw-bold text-dark">{formatFlatNoWithBlock(allot, data.block || allot.block, data)}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Location</div>
                                  <div className="fw-bold text-dark">{allot.location || allot.locationName || data.locationPreference1 || "-"}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Category</div>
                                  <div className="fw-bold text-dark">{resolveFlatCategory(allot, data.block || allot.block, data)}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Pool</div>
                                  <div className="fw-bold text-dark">{allot.pool || allot.poolName || allot.poolType || data.pool || "-"}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Quarter</div>
                                  <div className="fw-bold text-dark">{allot.quarterType || allot.quarter || data.quarterTypeEligibility?.quarterType || "-"}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Allotment Date</div>
                                  <div className="fw-bold text-dark">{formatDate(allot.allotmentDate || data.allotmentDate)}</div>
                                </div>
                                <div className="col-md-4 col-sm-6">
                                  <div className="text-muted small">Status</div>
                                  <div>
                                    <span className="badge bg-success">
                                      {data.occupationDetails?.status === 'APPROVED' ? 'OCCUPIED' : (allot.status || "ALLOTTED")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="row g-4 mb-3">
                          <div className="col-12 mt-2">
                            <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-2" style={{ fontSize: '15px' }}>
                              <i className="ti ti-calculator text-primary" style={{ fontSize: '18px' }}></i> Financial Information
                            </h6>
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}>
                              <div className="row g-3 align-items-center">
                                <div className="col-md-6 col-sm-6">
                                  <span className="text-muted d-block mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Occupation Date</span>
                                  <div className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: '14px' }}>
                                    <i className="ti ti-calendar text-muted me-1" style={{ fontSize: '16px' }}></i>
                                    {financeObj?.occupationDate || financeObj?.OccupationDate || data?.occupationDetails?.occupationDate || data?.occupationDate ? formatDate(financeObj?.occupationDate || financeObj?.OccupationDate || data?.occupationDetails?.occupationDate || data?.occupationDate) : "-"}
                                  </div>
                                </div>
                                <div className="col-md-6 col-sm-6">
                                  <span className="text-muted d-block mb-1" style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>License Fees</span>
                                  <div className="fw-bold text-primary" style={{ fontSize: '16px', fontWeight: '700' }}>
                                    {(() => {
                                      const licType = financeObj?.licenseType || financeObj?.LicenseType || financeObj?.licanceType || financeObj?.LicanceType || (financeApprovers?.find((a: any) => a.licenseType || a.LicenseType)?.licenseType) || data?.licenseType || "";
                                      const feeVal = financeObj?.LicenseFees ?? financeObj?.licenseFees ?? financeObj?.LicenceFees ?? financeObj?.licenceFees ?? (financeApprovers?.find((a: any) => a.LicenseFees !== undefined || a.licenseFees !== undefined || a.LicenceFees !== undefined || a.licenceFees !== undefined)?.LicenseFees || financeApprovers?.find((a: any) => a.LicenseFees !== undefined || a.licenseFees !== undefined || a.LicenceFees !== undefined || a.licenceFees !== undefined)?.licenseFees) ?? data?.licenseFees;
                                      const formattedFeeStr = financeObj?.formattedFee || (feeVal !== undefined && feeVal !== null && feeVal !== "" ? `₹${Number(feeVal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "");

                                      if (licType && formattedFeeStr) {
                                        return `${licType} - ${formattedFeeStr}`;
                                      } else if (licType) {
                                        return licType;
                                      } else if (formattedFeeStr) {
                                        return formattedFeeStr;
                                      }
                                      return "₹0.00";
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {financeObj?.remarks && (
                            <div className="col-12">
                              <div className="text-muted small mb-1">Remarks</div>
                              <div className="p-2 px-3 rounded bg-light border text-dark" style={{ wordBreak: 'break-word', fontSize: '13.5px' }}>
                                {financeObj.remarks}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Finance Approval Flow & Remarks */}
                        {financeApprovers.filter((appr: any) => (appr.status || "").toUpperCase() !== "PENDING" || (appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null")).length > 0 && (
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-2">
                              <i className="ti ti-user-check text-primary"></i> Finance Approver Workflow
                            </h6>
                            <div className="d-flex flex-column gap-2">
                              {financeApprovers
                                .filter((appr: any) => (appr.status || "").toUpperCase() !== "PENDING" || (appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null"))
                                .map((appr: any, index: number) => (
                                  <div key={index} className="card border-0 shadow-none mb-0" style={{ borderRadius: '8px', border: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
                                    <div className="card-body p-2 px-3">
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center">
                                          <div
                                            className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden"
                                            style={{ width: '38px', height: '38px', border: '1px solid #f1f5f9' }}
                                          >
                                            {appr.image ? (
                                              <img
                                                src={appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`}
                                                alt={appr.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              />
                                            ) : (
                                              <i className="ti ti-user text-muted" style={{ fontSize: '16px' }}></i>
                                            )}
                                          </div>
                                          <div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>
                                              {appr.name}
                                              {appr.empId && <span className="text-muted fw-normal small ms-1" style={{ fontSize: '11px' }}>({appr.empId})</span>}
                                            </div>
                                            <div className="text-muted extra-small" style={{ fontSize: '11px' }}>
                                              {appr.approvedAt ? formatDate(appr.approvedAt, true) : "Awaiting response"}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-end">
                                          <span
                                            className={`badge ${appr.status?.toUpperCase() === 'APPROVED' ? 'bg-success-light text-success' :
                                              appr.status?.toUpperCase() === 'REJECTED' ? 'bg-danger-light text-danger' :
                                                'bg-warning-light text-warning'
                                              }`}
                                            style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                                          >
                                            {appr.status || "PENDING"}
                                          </span>
                                        </div>
                                      </div>
                                      {appr.remarks && String(appr.remarks).trim() !== "" && String(appr.remarks).toLowerCase() !== "null" && (
                                        <div
                                          className="p-2 px-3 rounded-2"
                                          style={{ backgroundColor: '#f8fafc', borderLeft: '3px solid #6366f1' }}
                                        >
                                          <div className="d-flex gap-2 align-items-start">
                                            <i className="ti ti-quote text-primary" style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                            <div className="text-secondary" style={{ fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                              {appr.remarks}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }] : [])
            ]}
          />
        </div>

        {/* ── Willingness Modal ─────────────────────────────────────────── */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-shield-check text-primary fs-5" />
              <span className="fw-bold">Willingness Confirmation</span>
            </div>
          }
          open={isModalOpen}
          onCancel={() => !isConfirming && setIsModalOpen(false)}
          footer={null}
          centered
          width={520}
          maskClosable={!isConfirming}
          zIndex={1050}
        >
          {/* Allotment summary */}
          <div
            className="p-3 rounded mb-4"
            style={{ background: "#f0f5ff", border: "1px solid #c7d9f8" }}
          >
            <div className="row g-3">
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Reference No</div>
                <div className="fw-bold small">{activeWillingnessDetail?.referenceNumber || "-"}</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Quarter Type</div>
                <div className="fw-bold small">{activeWillingnessDetail?.quarterType || "-"}</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Location</div>
                <div className="fw-bold small">{activeWillingnessDetail?.location_name || activeWillingnessDetail?.location || "-"}</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Block</div>
                <div className="fw-bold small">{activeWillingnessDetail?.block || "-"}</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Flat No (ID)</div>
                <div className="fw-bold small">{formatFlatNoWithBlock(activeWillingnessDetail, activeWillingnessDetail?.block || data?.block, data)} ({activeWillingnessDetail?.flatId || activeWillingnessDetail?.flatDetails?.id || "-"})</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Pool</div>
                <div className="fw-bold small">{activeWillingnessDetail?.pool_name || activeWillingnessDetail?.poolName || "-"}</div>
              </div>
              <div className="col-4">
                <div className="extra-small text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: 1 }}>Category</div>
                <div className="fw-bold small">{resolveFlatCategory(activeWillingnessDetail, activeWillingnessDetail?.block || data?.block, data)}</div>
              </div>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleWillingnessSubmit}
            initialValues={{ isWilling: "YES" }}
          >
            <Form.Item
              name="isWilling"
              label={<span className="fw-semibold">Are you willing to accept this allotment?</span>}
              rules={[{ required: true, message: "Please select your willingness" }]}
            >
              <Radio.Group
                className="w-100"
                onChange={(e) => setSelectedWillingness(e.target.value)}
              >
                <Space direction="vertical" className="w-100">
                  <div
                    className="p-3 rounded d-flex align-items-center gap-3"
                    style={{
                      border: `2px solid ${selectedWillingness === "YES" ? "#0b57b7" : "#dee2e6"}`,
                      background: selectedWillingness === "YES" ? "#eaf3ff" : "#fff",
                      cursor: "pointer",
                      transition: "all .2s",
                    }}
                    onClick={() => {
                      setSelectedWillingness("YES");
                      form.setFieldsValue({ isWilling: "YES" });
                    }}
                  >
                    <Radio value="YES" />
                    <div>
                      <div className="fw-bold small">Yes, I am Willing</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        I accept this allotment and agree to follow the applicable rules.
                      </div>
                    </div>
                  </div>

                  <div
                    className="p-3 rounded d-flex align-items-center gap-3"
                    style={{
                      border: `2px solid ${selectedWillingness === "NO" ? "#dc3545" : "#dee2e6"}`,
                      background: selectedWillingness === "NO" ? "#fff0f1" : "#fff",
                      cursor: "pointer",
                      transition: "all .2s",
                    }}
                    onClick={() => {
                      setSelectedWillingness("NO");
                      form.setFieldsValue({ isWilling: "NO" });
                    }}
                  >
                    <Radio value="NO" />
                    <div>
                      <div className="fw-bold small">No, I am Not Willing</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        I decline this allotment. This action cannot be undone.
                      </div>
                    </div>
                  </div>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="declaration"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                        new Error("You must accept the declaration to proceed")
                      ),
                },
              ]}
            >
              <Checkbox className="small">
                I confirm my choice above is final and I have read all relevant policies regarding quarter allotment.
              </Checkbox>
            </Form.Item>

            <div className="d-flex gap-2 pt-3 border-top justify-content-end">
              <Button
                disabled={isConfirming}
                onClick={() => setIsModalOpen(false)}
                className="rounded-pill px-4"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isConfirming}
                className="rounded-pill px-5"
              >
                Submit Confirmation
              </Button>
            </div>
          </Form>
        </Modal>

        {showLocationModal && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-bold text-dark">Update Location Preferences</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowLocationModal(false);
                      setModalErrors({});
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {isFetchingLocations ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                      <span className="ms-2">Loading locations...</span>
                    </div>
                  ) : (
                    <form onSubmit={handleUpdateLocations}>
                      <div className="mb-3">
                        <label className="form-label small fw-semibold text-dark">Location Preference 1 <span className="text-danger">*</span></label>
                        <select
                          className="form-select form-select-sm"
                          value={modalPrefs.pref1}
                          onChange={(e) => {
                            const val = e.target.value;
                            setModalPrefs(prev => ({
                              ...prev,
                              pref1: val,
                              pref2: prev.pref2 === val ? "" : prev.pref2,
                              pref3: prev.pref3 === val ? "" : prev.pref3
                            }));
                          }}
                          required
                        >
                          <option value="">Select Location</option>
                          {availableLocations
                            .filter((loc: any) => String(loc.id) !== modalPrefs.pref2 && String(loc.id) !== modalPrefs.pref3)
                            .map((loc: any) => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold text-dark">Location Preference 2</label>
                        <select
                          className="form-select form-select-sm"
                          value={modalPrefs.pref2}
                          onChange={(e) => {
                            const val = e.target.value;
                            setModalPrefs(prev => ({
                              ...prev,
                              pref2: val,
                              pref3: prev.pref3 === val ? "" : prev.pref3
                            }));
                          }}
                          disabled={!modalPrefs.pref1}
                        >
                          <option value="">Select Location (Optional)</option>
                          {availableLocations
                            .filter((loc: any) => String(loc.id) !== modalPrefs.pref1 && String(loc.id) !== modalPrefs.pref3)
                            .map((loc: any) => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold text-dark">Location Preference 3</label>
                        <select
                          className="form-select form-select-sm"
                          value={modalPrefs.pref3}
                          onChange={(e) => setModalPrefs(prev => ({ ...prev, pref3: e.target.value }))}
                          disabled={!modalPrefs.pref2}
                        >
                          <option value="">Select Location (Optional)</option>
                          {availableLocations
                            .filter((loc: any) => String(loc.id) !== modalPrefs.pref1 && String(loc.id) !== modalPrefs.pref2)
                            .map((loc: any) => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                      </div>

                      {modalErrors.submit && (
                        <div className="alert alert-danger py-2 px-3 small">{modalErrors.submit}</div>
                      )}

                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() => {
                            setShowLocationModal(false);
                            setModalErrors({});
                          }}
                          disabled={isUpdatingLocations}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                          disabled={isUpdatingLocations || !modalPrefs.pref1}
                        >
                          {isUpdatingLocations && (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          )}
                          Update Preferences
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* <div className="mb-3">
              <h6>Actions</h6>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary">
                  <i className="ti ti-file-text me-1"></i> View Application Form
                </button>
                {data.status?.toUpperCase() === "APPROVED" && (
                  <button className="btn btn-danger">
                    <i className="ti ti-download me-1"></i> Download Allotment
                    Letter
                  </button>
                )}
              </div>
            </div> */}


      </div>

      {/* Staff Quarters Allotment Order Customizer & PDF Download Modal */}
      <AllotmentLetterCustomizerModal
        open={letterCustomizerOpen}
        onClose={() => setLetterCustomizerOpen(false)}
        allotmentData={{
          ...apiData,
          ...(apiData?.allotmentDetails || {}),
          ...(apiData?.allotment || {}),
          allotmentId: apiData?.allotmentDetails?.id || apiData?.allotment?.id || apiData?.id,
          registrationId: apiData?.id || id,
          employeeName: apiData?.employeeName || apiData?.employee_name,
          employeeNumber: apiData?.empId || apiData?.employeeNumber,
          quarterType: apiData?.allotmentDetails?.quarterType || apiData?.quarterType || apiData?.quarter_type,
          flatNumber: formatFlatNoWithBlock(apiData?.allotmentDetails, apiData?.block, apiData) || apiData?.allotmentDetails?.flatNumber || apiData?.allotmentDetails?.flatNo,
          location: apiData?.allotmentDetails?.location || apiData?.location || apiData?.location_name,
          pool: apiData?.pool?.name || apiData?.poolName || apiData?.pool || "OPN",
        }}
      />
    </div>
  );
};

export default StaffQuarterDetails;
